// src/components/Collaborations.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Chip,
  Alert,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import TopBar from './TopBar';
import { RoundedCard } from './shared/StyledComponents';
import HandshakeIcon from '@mui/icons-material/Handshake';
import StorefrontIcon from '@mui/icons-material/Storefront';
import GroupsIcon from '@mui/icons-material/Groups';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import VisibilityIcon from '@mui/icons-material/Visibility';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled(Box)(() => ({
  minHeight: '100vh',
  backgroundColor: '#fafafa',
  display: 'flex',
  flexDirection: 'column',
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  flex: 1,
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(6),
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(4),
  },
}))

const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
  borderRadius: 24,
  padding: theme.spacing(6),
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
  position: 'relative',
  overflow: 'hidden',
  animation: `${fadeIn} 0.5s ease-out`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-10%',
    width: 400,
    height: 400,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(255,183,27,0.15) 0%, transparent 70%)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-30%',
    left: '-5%',
    width: 300,
    height: 300,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(255,183,27,0.1) 0%, transparent 70%)',
  },
}));

const BenefitCard = styled(RoundedCard)(({ theme }) => ({
  background: '#ffffff',
  border: '1px solid #e8e8e8',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: `${fadeIn} 0.4s ease-out`,
  height: '100%',
  '&:hover': {
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
    transform: 'translateY(-4px)',
    borderColor: '#ffb71b',
  },
}));

const IconCircle = styled(Box)(({ $bgColor = '#fff8e7' }) => ({
  width: 56,
  height: 56,
  borderRadius: 16,
  backgroundColor: $bgColor,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 16,
}));

const SignUpButton = styled(Button)(() => ({
  borderRadius: 16,
  textTransform: 'none',
  fontWeight: 700,
  padding: '16px 48px',
  fontSize: '1.1rem',
  background: 'linear-gradient(135deg, #ffb71b 0%, #ff9500 100%)',
  color: '#000',
  boxShadow: '0 4px 16px rgba(255, 183, 27, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #ffc94d 0%, #ffb71b 100%)',
    boxShadow: '0 6px 24px rgba(255, 183, 27, 0.4)',
    transform: 'translateY(-2px)',
  },
  transition: 'all 0.2s ease',
}));

const DEFAULT_CONTENT = {
  heroHeading: 'Collaborations',
  heroBadge: 'New Feature',
  heroDescription: 'Collaborate with other businesses around you and offer their customers a promotion when they earn a reward. Using this feature will help you expose yourself to other potential customers in your area.',
  heroChip: 'You can only collaborate with businesses in different categories to you',
  benefitsHeading: 'How It Works',
  benefits: [
    { title: 'Increase Your Visibility', description: 'Get your business in front of new customers who are already engaged with loyalty programs in your area.' },
    { title: 'Reach New Customers', description: 'When a customer earns a reward at a partner business, they see your promotion too. Tap into an engaged audience.' },
    { title: 'Cross-Promote Offers', description: 'Offer exclusive promotions to customers from collaborating businesses. A coffee shop pairs with a bakery, a gym pairs with a smoothie bar.' },
    { title: 'Grow Together', description: 'Build a network of complementary businesses in your area. More collaborations means more exposure for everyone involved.' },
  ],
  stepsHeading: 'What Happens When You Sign Up',
  steps: [
    'Your business becomes visible to other businesses in your area looking to collaborate.',
    'Businesses in different categories can send you a collaboration request.',
    'When you accept, their customers will see your promotion when they earn a reward - and vice versa.',
    'You set the promotion you want to offer to their customers.',
    'Track new customers coming from collaborations right from your dashboard.',
  ],
  ctaHeading: 'Ready to grow your customer base?',
  ctaSubtext: "Sign up to start collaborating with businesses near you. It's free and you can opt out at any time.",
  ctaButton: 'Sign Up for Collaborations',
  successTitle: "You're signed up for Collaborations!",
  successText: "We'll notify you when businesses in your area are available to collaborate with. Stay tuned!",
};

const BENEFIT_ICONS = [
  { icon: <VisibilityIcon sx={{ color: '#1976d2', fontSize: 28 }} />, bgColor: '#e3f2fd' },
  { icon: <GroupsIcon sx={{ color: '#9c27b0', fontSize: 28 }} />, bgColor: '#f3e5f5' },
  { icon: <LocalOfferIcon sx={{ color: '#e91e63', fontSize: 28 }} />, bgColor: '#fce4ec' },
  { icon: <TrendingUpIcon sx={{ color: '#4caf50', fontSize: 28 }} />, bgColor: '#e8f5e9' },
];

export default function Collaborations() {
  const [signedUp, setSignedUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState(DEFAULT_CONTENT);

  // Get userId from URL
  const pathParts = window.location.pathname.split('/');
  const userId = pathParts[pathParts.length - 1];

  // Check if already opted in + load CMS content
  useEffect(() => {
    if (userId) {
      const token = localStorage.getItem('token');
      fetch(`/api/vendor/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data?.vendor?.collaboration_opted_in) setSignedUp(true);
        })
        .catch(() => {});
    }

    fetch('/api/collaborations-content')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && Object.keys(data).length > 1) {
          setContent({
            heroHeading: data.heroHeading || DEFAULT_CONTENT.heroHeading,
            heroBadge: data.heroBadge || DEFAULT_CONTENT.heroBadge,
            heroDescription: data.heroDescription || DEFAULT_CONTENT.heroDescription,
            heroChip: data.heroChip || DEFAULT_CONTENT.heroChip,
            benefitsHeading: data.benefitsHeading || DEFAULT_CONTENT.benefitsHeading,
            benefits: data.benefits && data.benefits.length > 0 ? data.benefits : DEFAULT_CONTENT.benefits,
            stepsHeading: data.stepsHeading || DEFAULT_CONTENT.stepsHeading,
            steps: data.steps && data.steps.length > 0 ? data.steps : DEFAULT_CONTENT.steps,
            ctaHeading: data.ctaHeading || DEFAULT_CONTENT.ctaHeading,
            ctaSubtext: data.ctaSubtext || DEFAULT_CONTENT.ctaSubtext,
            ctaButton: data.ctaButton || DEFAULT_CONTENT.ctaButton,
            successTitle: data.successTitle || DEFAULT_CONTENT.successTitle,
            successText: data.successText || DEFAULT_CONTENT.successText,
          });
        }
      })
      .catch(() => {});
  }, [userId]);

  const handleSignUp = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/vendor/${userId}/collaboration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ opted_in: true }),
      });
      if (res.ok) setSignedUp(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <TopBar />
      <ContentContainer maxWidth="xl">
        {/* Hero Section */}
        <HeroSection>
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: { xs: 1.5, sm: 2 }, mb: 2 }}>
              <HandshakeIcon sx={{ color: '#ffb71b', fontSize: { xs: 30, sm: 40 } }} />
              <Chip
                icon={<ScheduleIcon sx={{ color: '#1a1a2e !important', fontSize: '16px !important' }} />}
                label="Coming Soon"
                sx={{
                  backgroundColor: '#ffb71b',
                  color: '#1a1a2e',
                  fontWeight: 800,
                  fontSize: '0.8rem',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}
              />
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#fff', mb: 2 }}>
              {content.heroHeading}
            </Typography>
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.8)', maxWidth: 700, lineHeight: 1.8, fontWeight: 400 }}>
              {content.heroDescription}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Chip
                icon={<StorefrontIcon sx={{ color: '#ffb71b !important' }} />}
                label={content.heroChip}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.9)',
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  py: 2.5,
                  px: 1,
                }}
              />
            </Box>
          </Box>
        </HeroSection>

        {/* Benefits Grid */}
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#222', mb: 3 }}>
          {content.benefitsHeading}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
            gap: 3,
            mb: 5,
          }}
        >
          {content.benefits.map((benefit, index) => {
            const iconSet = BENEFIT_ICONS[index % BENEFIT_ICONS.length];
            return (
              <BenefitCard key={index} sx={{ p: 3 }}>
                <IconCircle $bgColor={iconSet.bgColor}>
                  {iconSet.icon}
                </IconCircle>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#222', mb: 1, fontSize: '1rem' }}>
                  {benefit.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.7 }}>
                  {benefit.description}
                </Typography>
              </BenefitCard>
            );
          })}
        </Box>

        {/* How Collaborations Work */}
        <RoundedCard sx={{ p: 4, mb: 4, background: '#ffffff', border: '1px solid #e8e8e8' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#222', mb: 3 }}>
            {content.stepsHeading}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {content.steps.map((step, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    backgroundColor: '#fff8e7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: 0.25,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#ffb71b' }}>
                    {index + 1}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.6 }}>
                  {step}
                </Typography>
              </Box>
            ))}
          </Box>
        </RoundedCard>

        {/* Sign Up Section */}
        <Box sx={{ textAlign: 'center', py: 4 }}>
          {signedUp ? (
            <Alert
              icon={<CheckCircleOutlineIcon sx={{ fontSize: 28 }} />}
              severity="success"
              sx={{
                maxWidth: 600,
                mx: 'auto',
                borderRadius: 3,
                fontSize: '1rem',
                py: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                {content.successTitle}
              </Typography>
              <Typography variant="body2">
                {content.successText}
              </Typography>
            </Alert>
          ) : (
            <>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#222', mb: 1 }}>
                {content.ctaHeading}
              </Typography>
              <Typography variant="body1" sx={{ color: '#666', mb: 4, maxWidth: 500, mx: 'auto' }}>
                {content.ctaSubtext}
              </Typography>
              <SignUpButton onClick={handleSignUp} disabled={loading}>
                {loading ? 'Signing up...' : content.ctaButton}
              </SignUpButton>
              <Typography
                variant="caption"
                sx={{ display: 'block', color: '#767676', mt: 1.5 }}
              >
                This registers your interest. Collaborations aren&rsquo;t available yet.
              </Typography>
            </>
          )}
        </Box>
      </ContentContainer>
    </PageContainer>
  );
}
